import React, { useState, useEffect, useRef } from 'react';
import { Server, Plus, Edit, Trash2, CheckCircle, Loader2, AlertCircle, Activity, Globe, X, MoreVertical, Lock, Crown, ArrowUpRight, ChevronDown, BarChart2, Trash, Calendar, Zap, Clock, TrendingUp, TrendingDown, Link2, FileText } from 'lucide-react';
import { COUNTRIES, getCountryByCode } from '../utils/countries';
import CountryFlag from './CountryFlag';
import { usePermissions } from '../hooks/usePermissions';
import { LimitWarning } from './UpgradeRequired';
import SubscriptionExpiredOverlay from './SubscriptionExpiredOverlay';

interface ServerData {
  id: number;
  name: string;
  host: string;
  port: number;
  username: string;
  base_path: string;
  country?: string;
  ping?: number;
  is_active: boolean;
  created_at: string;
  last_connection?: string;
}

// Server statistics interface
interface DomainActivation {
  domain: string;
  count: number;
}

interface RecentOperation {
  type: string;
  path: string;
  status: string;
  date: string;
}

interface ServerStats {
  totalActivations: number;
  activePages: number;
  cleanedFiles: number;
  lastCleanup: string | null;
  activationsLast7Days: number[];
  activationsLast30Days: number[];
  domains: number;
  // Extended stats
  peakHour: number | null;
  peakHourActivations: number;
  avgActivationDuration: number; // in hours
  weekTrend: number; // percentage change
  domainStats: DomainActivation[];
  recentOperations: RecentOperation[];
}

const ServersPanel: React.FC = () => {
  const [servers, setServers] = useState<ServerData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingServer, setEditingServer] = useState<ServerData | null>(null);
  const [testingConnection, setTestingConnection] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);  // Loading state for server creation
  const [toast, setToast] = useState<{ message: string, type: 'success' | 'error' } | null>(null);
  const [showDomainsModal, setShowDomainsModal] = useState<{ serverId: number, serverName: string } | null>(null);
  const [serverDomains, setServerDomains] = useState<any[]>([]);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const pingIntervals = useRef<{ [key: number]: NodeJS.Timeout }>({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Server statistics expansion
  const [expandedServer, setExpandedServer] = useState<number | null>(null);
  const [serverStats, setServerStats] = useState<{ [key: number]: ServerStats }>({});
  const [loadingStats, setLoadingStats] = useState<number | null>(null);

  // Permissions
  const { getLimit, isLimitReached, getLimitWarning, isStarter, canAccess, isExpired } = usePermissions();
  const canAccessStats = canAccess('monitoring'); // Pro+ only
  const serverLimit = getLimit('servers');
  const limitReached = isLimitReached('servers', servers.length);
  const limitWarning = getLimitWarning('servers', servers.length);

  const [formData, setFormData] = useState({
    name: '', host: '', port: 22, username: '', password: '', private_key: '', base_path: '/var/www/html', country: ''
  });

  useEffect(() => {
    loadServers();
    return () => { Object.values(pingIntervals.current).forEach(i => clearInterval(i)); };
  }, []);

  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 3000); return () => clearTimeout(t); }
  }, [toast]);

  const loadServers = async () => {
    try {
      setIsLoading(true);
      const res = await fetch('/api/servers/my', { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setServers(data.servers);
        data.servers.forEach((s: ServerData) => startPingInterval(s.id));
      } else setError(data.error || 'Failed to load');
    } catch { setError('Failed to connect'); }
    finally { setIsLoading(false); }
  };

  const startPingInterval = (serverId: number) => {
    if (pingIntervals.current[serverId]) clearInterval(pingIntervals.current[serverId]);
    const ping = async () => {
      try {
        const res = await fetch(`/api/servers/${serverId}/ping`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) setServers(prev => prev.map(s => s.id === serverId ? { ...s, ping: data.ping } : s));
      } catch { }
    };
    ping();
    pingIntervals.current[serverId] = setInterval(ping, 35000); // 35 seconds
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);  // Start loading
    try {
      const url = editingServer ? `/api/servers/${editingServer.id}` : '/api/servers';
      const res = await fetch(url, {
        method: editingServer ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        setToast({ message: editingServer ? 'Updated!' : 'Added!', type: 'success' });
        loadServers();
        handleCloseModal();
      } else setToast({ message: data.error || 'Failed', type: 'error' });
    } catch { setToast({ message: 'Failed to save', type: 'error' }); }
    finally { setIsSaving(false); }  // Stop loading
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this server?')) return;
    try {
      const res = await fetch(`/api/servers/${id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (data.success) { setToast({ message: 'Deleted', type: 'success' }); loadServers(); }
      else setToast({ message: data.error || 'Failed', type: 'error' });
    } catch { setToast({ message: 'Failed to delete', type: 'error' }); }
  };

  const testConnection = async (id: number) => {
    setTestingConnection(id);
    try {
      const res = await fetch(`/api/servers/${id}/test`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      setToast({ message: data.success ? 'Connected!' : data.error || 'Failed', type: data.success ? 'success' : 'error' });
    } catch { setToast({ message: 'Test failed', type: 'error' }); }
    finally { setTestingConnection(null); }
  };

  const loadServerDomains = async (id: number, name: string) => {
    try {
      const res = await fetch(`/api/servers/${id}/domains`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) { setServerDomains(data.domains); setShowDomainsModal({ serverId: id, serverName: name }); }
    } catch { setToast({ message: 'Failed to load domains', type: 'error' }); }
  };

  // Handle server row click for stats expansion
  const handleServerClick = async (serverId: number) => {
    if (!canAccessStats) {
      setShowUpgradeModal(true);
      return;
    }

    if (expandedServer === serverId) {
      setExpandedServer(null);
      return;
    }

    setExpandedServer(serverId);

    // Load stats if not cached
    if (!serverStats[serverId]) {
      await loadServerStats(serverId);
    }
  };

  // Load server statistics
  const loadServerStats = async (serverId: number) => {
    setLoadingStats(serverId);
    try {
      const res = await fetch(`/api/servers/${serverId}/stats`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setServerStats(prev => ({ ...prev, [serverId]: data.stats }));
      }
    } catch {
      setToast({ message: 'Failed to load statistics', type: 'error' });
    } finally {
      setLoadingStats(null);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingServer(null);
    setFormData({ name: '', host: '', port: 22, username: '', password: '', private_key: '', base_path: '/var/www/html', country: '' });
  };

  const openEditModal = (s: ServerData) => {
    setEditingServer(s);
    setFormData({ name: s.name, host: s.host, port: s.port, username: s.username, password: '', private_key: '', base_path: s.base_path, country: s.country || '' });
    setShowAddModal(true);
  };

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="w-full min-h-full animate-fade-in grid-bg">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-lg flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white`}>
          {toast.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-12">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Ser<span className="gradient-text">vers</span></h1>
          <p className="text-white/50">Manage your server connections</p>
        </div>
        {servers.length > 0 && (
          limitReached ? (
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white/50 rounded-lg text-sm font-medium cursor-not-allowed"
              disabled
            >
              <Lock className="w-4 h-4" />
              Server Limit Reached
            </button>
          ) : (
            <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg text-sm font-medium hover:bg-white/90 transition-colors">
              <Plus className="w-4 h-4" />
              Add Server
            </button>
          )
        )}
      </div>

      {/* Limit Warning */}
      {limitWarning !== 'none' && (
        <div className="mb-6">
          <LimitWarning
            resource="servers"
            current={servers.length}
            limit={serverLimit}
            severity={limitWarning}
          />
        </div>
      )}

      {/* RU-only Warning for Starter */}
      {isStarter && (
        <div className="mb-6 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
          <p className="text-sm text-blue-400">
            <strong>Note:</strong> Your Starter plan only supports RU region servers. Upgrade to Pro for any region.
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-300"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Empty State */}
      {servers.length === 0 ? (
        <div className="rounded-lg bg-white/[0.02] border border-white/10 p-12 text-center animate-fade-in glow-card">
          <Server className="w-10 h-10 text-white/20 mx-auto mb-4" />
          <p className="text-white/60 mb-1">No servers yet</p>
          <p className="text-white/30 text-sm mb-6">Add your first server to start deploying</p>
          <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-white text-black rounded-lg text-sm font-medium hover:bg-white/90 transition-all duration-300">
            <Plus className="w-4 h-4 inline mr-2" />Add Server
          </button>
        </div>
      ) : (
        /* Servers List */
        <div className="rounded-lg bg-white/[0.02] border border-white/10 animate-fade-in glow-card">
          {servers.map((server, index) => {
            const country = getCountryByCode(server.country);
            const isExpanded = expandedServer === server.id;
            const stats = serverStats[server.id];
            const isLoadingThisStats = loadingStats === server.id;

            return (
              <div
                key={server.id}
                className="border-b border-white/5 last:border-b-0"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Server Row - Clickable */}
                <div
                  className="px-5 py-4 flex items-center justify-between hover:bg-white/[0.03] transition-all duration-300 cursor-pointer"
                  onClick={() => handleServerClick(server.id)}
                >
                  <div className="flex items-center gap-4">
                    {/* Expand indicator */}
                    <ChevronDown
                      className={`w-4 h-4 text-white/30 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                    />
                    {country ? (
                      <CountryFlag countryCode={server.country} alt={country.name} />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                        <Server className="w-5 h-5 text-white/40" />
                      </div>
                    )}
                    <div>
                      <span className="text-white font-medium">{server.name}</span>
                      <span className="text-white/30 ml-3 text-sm">{server.username}@{server.host}:{server.port}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {server.ping && server.ping > 0 ? (
                      <span className="text-green-400 text-sm font-mono">{server.ping}ms</span>
                    ) : (
                      <span className="text-white/20 text-sm">—</span>
                    )}
                    {/* Pro badge for stats */}
                    {!canAccessStats && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-purple-500/10 rounded text-xs text-purple-400">
                        <Lock className="w-3 h-3" />
                        Pro
                      </div>
                    )}
                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setOpenDropdown(openDropdown === server.id ? null : server.id)}
                        className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                      >
                        <MoreVertical className="w-4 h-4 text-white/40" />
                      </button>
                      {openDropdown === server.id && (
                        <div className="absolute right-0 top-full mt-1 w-44 bg-[#0a0a0a] border border-white/10 rounded-lg shadow-xl z-50 py-1 animate-fade-in">
                          <button
                            onClick={() => { testConnection(server.id); setOpenDropdown(null); }}
                            disabled={testingConnection === server.id}
                            className="w-full px-3 py-2 text-left text-sm text-white/70 hover:bg-white/5 flex items-center gap-3"
                          >
                            {testingConnection === server.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
                            Test connection
                          </button>
                          <button
                            onClick={() => { loadServerDomains(server.id, server.name); setOpenDropdown(null); }}
                            className="w-full px-3 py-2 text-left text-sm text-white/70 hover:bg-white/5 flex items-center gap-3"
                          >
                            <Globe className="w-4 h-4" />
                            View domains
                          </button>
                          <button
                            onClick={() => { openEditModal(server); setOpenDropdown(null); }}
                            className="w-full px-3 py-2 text-left text-sm text-white/70 hover:bg-white/5 flex items-center gap-3"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                          <div className="border-t border-white/5 my-1"></div>
                          <button
                            onClick={() => { handleDelete(server.id); setOpenDropdown(null); }}
                            className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-3"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Stats Panel */}
                <div
                  className="overflow-hidden transition-all duration-500 ease-in-out"
                  style={{
                    maxHeight: isExpanded ? '800px' : '0px',
                    opacity: isExpanded ? 1 : 0
                  }}
                >
                  <div className="px-5 py-4 bg-white/[0.01] border-t border-white/5 relative">
                    <SubscriptionExpiredOverlay visible={isExpired} featureName="Server Statistics" />

                    {isLoadingThisStats ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                      </div>
                    ) : stats ? (
                      <div className={`space-y-6 ${isExpired ? 'opacity-20 blur-sm pointer-events-none' : ''}`}>
                        {/* Row 1: Core Stats */}
                        <div className="grid grid-cols-4 gap-4">
                          {/* Total Activations */}
                          <div className="p-4 rounded-lg bg-white/[0.03] border border-white/10 hover:bg-white/[0.05] transition-colors">
                            <div className="flex items-center gap-2 text-white/50 text-xs mb-2">
                              <Zap className="w-3.5 h-3.5" />
                              Total Activations
                            </div>
                            <p className="text-2xl font-semibold text-white">{stats.totalActivations}</p>
                          </div>

                          {/* Active Pages */}
                          <div className="p-4 rounded-lg bg-white/[0.03] border border-white/10 hover:bg-white/[0.05] transition-colors">
                            <div className="flex items-center gap-2 text-white/50 text-xs mb-2">
                              <Activity className="w-3.5 h-3.5" />
                              Active Pages
                            </div>
                            <p className="text-2xl font-semibold text-green-400">{stats.activePages}</p>
                          </div>

                          {/* Cleaned Files */}
                          <div className="p-4 rounded-lg bg-white/[0.03] border border-white/10 hover:bg-white/[0.05] transition-colors">
                            <div className="flex items-center gap-2 text-white/50 text-xs mb-2">
                              <Trash className="w-3.5 h-3.5" />
                              Cleaned Files
                            </div>
                            <p className="text-2xl font-semibold text-white">{stats.cleanedFiles}</p>
                          </div>

                          {/* Domains */}
                          <div className="p-4 rounded-lg bg-white/[0.03] border border-white/10 hover:bg-white/[0.05] transition-colors">
                            <div className="flex items-center gap-2 text-white/50 text-xs mb-2">
                              <Globe className="w-3.5 h-3.5" />
                              Linked Domains
                            </div>
                            <p className="text-2xl font-semibold text-white">{stats.domains}</p>
                          </div>
                        </div>

                        {/* Row 2: Performance Stats */}
                        <div className="grid grid-cols-4 gap-4">
                          {/* Peak Hour */}
                          <div className="p-4 rounded-lg bg-white/[0.03] border border-white/10 hover:bg-white/[0.05] transition-colors">
                            <div className="flex items-center gap-2 text-white/50 text-xs mb-2">
                              <Clock className="w-3.5 h-3.5" />
                              Peak Hour
                            </div>
                            <p className="text-lg font-medium text-white">
                              {stats.peakHour !== null ? `${stats.peakHour}:00` : '—'}
                              {stats.peakHourActivations > 0 && (
                                <span className="text-sm text-white/40 ml-2">({stats.peakHourActivations})</span>
                              )}
                            </p>
                          </div>

                          {/* Avg Duration */}
                          <div className="p-4 rounded-lg bg-white/[0.03] border border-white/10 hover:bg-white/[0.05] transition-colors">
                            <div className="flex items-center gap-2 text-white/50 text-xs mb-2">
                              <Calendar className="w-3.5 h-3.5" />
                              Avg Duration
                            </div>
                            <p className="text-lg font-medium text-white">{stats.avgActivationDuration}h</p>
                          </div>

                          {/* Week Trend */}
                          <div className="p-4 rounded-lg bg-white/[0.03] border border-white/10 hover:bg-white/[0.05] transition-colors">
                            <div className="flex items-center gap-2 text-white/50 text-xs mb-2">
                              {stats.weekTrend >= 0 ? (
                                <TrendingUp className="w-3.5 h-3.5 text-green-400" />
                              ) : (
                                <TrendingDown className="w-3.5 h-3.5 text-red-400" />
                              )}
                              Week Trend
                            </div>
                            <p className={`text-lg font-medium ${stats.weekTrend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {stats.weekTrend >= 0 ? '+' : ''}{stats.weekTrend}%
                            </p>
                          </div>

                          {/* Last Cleanup */}
                          <div className="p-4 rounded-lg bg-white/[0.03] border border-white/10 hover:bg-white/[0.05] transition-colors">
                            <div className="flex items-center gap-2 text-white/50 text-xs mb-2">
                              <Trash className="w-3.5 h-3.5" />
                              Last Cleanup
                            </div>
                            <p className="text-sm font-medium text-white/80">
                              {stats.lastCleanup ? new Date(stats.lastCleanup).toLocaleDateString() : 'Never'}
                            </p>
                          </div>
                        </div>

                        {/* Row 3: Charts and Lists */}
                        <div className="grid grid-cols-2 gap-4">
                          {/* Activations Chart (7 days) */}
                          <div className="p-4 rounded-lg bg-white/[0.03] border border-white/10">
                            <div className="flex items-center gap-2 text-white/50 text-xs mb-3">
                              <BarChart2 className="w-3.5 h-3.5" />
                              Activations (Last 7 Days)
                            </div>
                            <div className="flex items-end gap-2 h-24">
                              {stats.activationsLast7Days?.map((count, i) => {
                                const max = Math.max(...stats.activationsLast7Days, 1);
                                const height = (count / max) * 100;
                                const days = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
                                const dayIndex = (new Date().getDay() - 6 + i + 7) % 7;
                                return (
                                  <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1 h-full">
                                    <div className="w-full flex-1 flex items-end">
                                      <div
                                        className="w-full bg-accent-blue transition-all duration-300 hover:bg-accent-blue-hover"
                                        style={{ height: `${Math.max(height, 5)}%` }}
                                        title={`${count} activations`}
                                      />
                                    </div>
                                    <span className="text-[10px] text-white/40">{days[dayIndex]}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Domain Stats */}
                          <div className="p-4 rounded-lg bg-white/[0.03] border border-white/10">
                            <div className="flex items-center gap-2 text-white/50 text-xs mb-3">
                              <Link2 className="w-3.5 h-3.5" />
                              Activations by Domain
                            </div>
                            {stats.domainStats && stats.domainStats.length > 0 ? (
                              <div className="space-y-2">
                                {stats.domainStats.map((ds, i) => (
                                  <div key={i} className="flex items-center justify-between hover:bg-white/[0.02] p-1 -m-1 rounded transition-colors">
                                    <span className="text-sm text-white/70 truncate max-w-[180px]">{ds.domain}</span>
                                    <span className="text-sm font-mono text-purple-400">{ds.count}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-white/30">No domain data</p>
                            )}
                          </div>
                        </div>

                        {/* Row 4: Recent Operations */}
                        {stats.recentOperations && stats.recentOperations.length > 0 && (
                          <div className="p-4 rounded-lg bg-white/[0.03] border border-white/10">
                            <div className="flex items-center gap-2 text-white/50 text-xs mb-3">
                              <FileText className="w-3.5 h-3.5" />
                              Recent Operations
                            </div>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {stats.recentOperations.map((op, i) => (
                                <div key={i} className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-2">
                                    <span className={`w-2 h-2 rounded-full ${op.status === 'active' ? 'bg-green-400' : 'bg-white/20'}`} />
                                    <span className="text-white/70 font-mono text-xs">{op.path}</span>
                                  </div>
                                  <span className="text-white/30 text-xs">
                                    {new Date(op.date).toLocaleDateString()}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-white/40">
                        No statistics available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Domains Modal */}
      {showDomainsModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-black border border-white/10 rounded-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-white">Domains on {showDomainsModal.serverName}</h3>
                <p className="text-sm text-white/40">{serverDomains.length} domain(s)</p>
              </div>
              <button onClick={() => { setShowDomainsModal(null); setServerDomains([]); }} className="p-2 hover:bg-white/5 rounded-lg">
                <X className="w-5 h-5 text-white/40" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              {serverDomains.length === 0 ? (
                <div className="text-center py-12">
                  <Globe className="w-10 h-10 text-white/20 mx-auto mb-4" />
                  <p className="text-white/40">No domains found</p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {serverDomains.map((d) => (
                    <div key={d.id} className="py-3 flex items-center justify-between">
                      <span className="text-white">{d.domain}</span>
                      <span className={`text-xs ${d.is_active ? 'text-green-400' : 'text-white/30'}`}>{d.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-black border border-white/10 rounded-xl max-w-md w-full">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-medium text-white">{editingServer ? 'Edit Server' : 'Add Server'}</h3>
              <button onClick={handleCloseModal} className="p-2 hover:bg-white/5 rounded-lg"><X className="w-5 h-5 text-white/40" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm text-white/50 mb-2">Name</label>
                <input type="text" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-white/30" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div>
                <label className="block text-sm text-white/50 mb-2">Host</label>
                <input type="text" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-white/30" value={formData.host} onChange={(e) => setFormData({ ...formData, host: e.target.value })} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/50 mb-2">Port</label>
                  <input type="number" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-white/30" value={formData.port} onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })} required />
                </div>
                <div>
                  <label className="block text-sm text-white/50 mb-2">Username</label>
                  <input type="text" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-white/30" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required />
                </div>
              </div>
              <div>
                <label className="block text-sm text-white/50 mb-2">Password</label>
                <input type="password" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-white/30" placeholder={editingServer ? "Leave empty to keep" : ""} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!editingServer} />
              </div>
              <div>
                <label className="block text-sm text-white/50 mb-2">Base Path</label>
                <input type="text" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-white/30" value={formData.base_path} onChange={(e) => setFormData({ ...formData, base_path: e.target.value })} required />
              </div>
              <div>
                <label className="block text-sm text-white/50 mb-2">Country</label>
                <select className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-white/30" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })}>
                  <option value="">Select (optional)</option>
                  {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={isSaving}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${isSaving ? 'bg-white/50 cursor-not-allowed' : 'bg-white text-black hover:bg-white/90'}`}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {editingServer ? 'Updating...' : 'Setting up server...'}
                    </>
                  ) : (
                    editingServer ? 'Update' : 'Add'
                  )}
                </button>
                <button type="button" onClick={handleCloseModal} disabled={isSaving} className="flex-1 py-2 border border-white/10 text-white/70 rounded-lg text-sm hover:bg-white/5 transition-colors disabled:opacity-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowUpgradeModal(false)}>
          <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-8 max-w-sm w-full mx-4 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-white/5 flex items-center justify-center">
              <Lock className="w-8 h-8 text-white/40" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Server Limit Reached</h3>
            <p className="text-white/50 text-sm mb-6">
              You've reached your limit of {serverLimit} servers. Upgrade to add more.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1 py-2.5 text-white/60 hover:text-white border border-white/10 rounded-lg hover:border-white/20 transition-colors text-sm"
              >
                Cancel
              </button>
              <a
                href="https://ikcore.cloud/pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-2.5 bg-white text-black rounded-lg text-sm font-medium hover:bg-white/90 transition-colors flex items-center justify-center gap-2"
              >
                <Crown className="w-4 h-4" />
                Upgrade
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServersPanel;